import { Injectable, inject } from '@angular/core';
import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { PlatformChartService } from '@data/_generated-api/platform-api/services/platform-chart.service';
import { PlatformChartListItemDto } from '@data/_generated-api/platform-api/models/platform-chart-list-item-dto';
import { EXPECTED_API_ERRORS } from '@data/_generated-api/http-interceptors/silent-api-errors';
import { HdBodygraphChartPreset } from '../bodygraph/hd-bodygraph.models';
import { HdChartShareChange, HdSharedChartLoadError, HdSharedChartResult } from './hd-chart-share.models';

/**
 * Chart delen via link (spec 2026-10-20-chart-share-analyse §4.1): deellink maken en
 * intrekken (eigenaar), het overzicht van eigen gedeelde charts met "alles stoppen", en
 * een gedeelde chart ophalen op haar token (anoniem endpoint; met een JWT erbij komt het
 * volledige document en de eigenaar-vlag mee).
 */
@Injectable({ providedIn: 'root' })
export class HdChartShareService {
  private readonly chartApi = inject(PlatformChartService);

  async share(chartId: string): Promise<HdChartShareChange> {
    const result = await firstValueFrom(this.chartApi.postChartsActionShare({ body: { chartId } }));
    if (!result?.isValid || !result.value) {
      throw new Error(result?.simpleMessage || 'De chart kon niet gedeeld worden.');
    }
    return { id: chartId, shareToken: result.value.shareToken ?? null };
  }

  async unshare(chartId: string): Promise<HdChartShareChange> {
    const result = await firstValueFrom(this.chartApi.postChartsActionUnshare({ body: { chartId } }));
    if (!result?.isValid) {
      throw new Error(result?.simpleMessage || 'Het delen kon niet gestopt worden.');
    }
    return { id: chartId, shareToken: null };
  }

  /** Eigen gedeelde (niet-verwijderde) charts, laatst gedeeld eerst. */
  async listSharedByMe(): Promise<PlatformChartListItemDto[]> {
    const result = await firstValueFrom(this.chartApi.getChartsSharedByMe());
    if (!result?.isValid) {
      throw new Error(result?.simpleMessage || 'De gedeelde charts konden niet geladen worden.');
    }
    return result.value ?? [];
  }

  /** Alle deellinks intrekken (ook van charts in de prullenbak); geeft het aantal terug. */
  async unshareAll(): Promise<number> {
    const result = await firstValueFrom(this.chartApi.postChartsActionUnshareAll());
    if (!result?.isValid) {
      throw new Error(result?.simpleMessage || 'Het delen kon niet gestopt worden.');
    }
    return result.value ?? 0;
  }

  /**
   * Gedeelde chart op token. Een ongeldige of ingetrokken link en de anonieme limiet
   * (429) zijn normale uitkomsten: de interceptor laat ze als HttpErrorResponse door
   * (geen toast, geen Sentry) en de aanroeper krijgt een getypte laadfout.
   */
  async getShared(token: string): Promise<HdSharedChartResult> {
    const expected = new HttpContext().set(EXPECTED_API_ERRORS, true);
    let result;
    try {
      result = await firstValueFrom(this.chartApi.getChartsSharedToken({ token }, expected));
    } catch (e) {
      if (e instanceof HttpErrorResponse && e.status === 429) {
        throw new HdSharedChartLoadError('tooManyRequests', 'Deze deellink is net vaak geopend; probeer het zo opnieuw.');
      }
      throw new HdSharedChartLoadError('invalid', 'Deze deellink is niet (meer) geldig.');
    }
    const meta = result?.value;
    if (!result?.isValid || !meta?.document) {
      throw new HdSharedChartLoadError('invalid', result?.simpleMessage || 'Deze deellink is niet (meer) geldig.');
    }
    return { meta, preset: JSON.parse(meta.document) as HdBodygraphChartPreset };
  }
}
